import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Fade,
  CircularProgress,
  IconButton,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { globalConfig } from '../configuration/config';
import mainIcon from '../images/main_icon.jpg';
import { useTranslation } from "react-i18next";

const ImportModelsModal = ({ onCloseMenu, open, setOpen, projectGuid }) => {
  const [localOpen, setLocalOpen] = useState(!!projectGuid);
  const isControlled = open !== undefined && setOpen !== undefined;
  const isModalOpen = isControlled ? open : localOpen;
  const setIsModalOpen = isControlled ? setOpen : setLocalOpen;

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [modelName, setModelName] = useState('');
  const [modelNameError, setModelNameError] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [scale, setScale] = useState('1');
  const [rotationY, setRotationY] = useState(0);
  const { t } = useTranslation();

  const handleScaleChange = (event) => setScale(event.target.value);
  const handleRotationChange = (event) => setRotationY(parseInt(event.target.value, 10));

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!projectGuid) return;
    setLocalOpen(true);
  }, [projectGuid]);

  useEffect(() => {
    if (selectedFiles.length > 0) {
      const defaultName = selectedFiles.find(
        (file) => file.name.endsWith('.3ds') || file.name.endsWith('.blend'),
      )?.name;
      if (defaultName) setModelName(defaultName.replace(/\.[^/.]+$/, ''));
    }
  }, [selectedFiles]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(files);
      setUploadMessage('');
      setLoading(false);
      setIsUploadComplete(false);
      setUploadProgress(0);
      setModelNameError('');
    }
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      setUploadMessage(t('importModels.messages.chooseFiles'));
      return;
    }
    if (!modelName.trim()) {
      setModelNameError(t('importModels.errors.nameRequired'));
      return;
    }

    setLoading(true);
    setUploadMessage('');
    setUploadProgress(0);
    setIsUploadComplete(false);

    const storage = localStorage.getItem('company');
    const storageCompany = JSON.parse(storage || 'null');

    const formData = new FormData();
    formData.append('IdCompany', storageCompany?.id || 1);
    formData.append('name', modelName);
    formData.append('Scale', scale);
    formData.append('RotationY', rotationY);
    if (projectGuid) formData.append('ProjectGuid', projectGuid);
    selectedFiles.forEach((file) => formData.append('Files', file));

    const maxRetries = 3;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        await axios.post(`${globalConfig.config.common.api}Projects/ImportModels`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(progress);
            }
          },
        });

        setUploadMessage(
          <>
            {t('importModels.messages.allFilesUploaded')}{' '}
            {t('importModels.messages.chatWillBeCreatedWithImage')}{' '}
            <img
              src={mainIcon}
              alt="Conversion Icon"
              style={{ width: 50, height: 50, verticalAlign: 'sub', margin: '0 5px' }}
            />{' '}
            {t('importModels.messages.importProcessShown')}{' '}
            {t('importModels.messages.resultInSameChat')}
          </>
        );
        setSelectedFiles([]);
        setModelName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setIsUploadComplete(true);
        return;
      } catch (error) {
        attempt += 1;
        console.error(`Attempt ${attempt} failed:`, error);
        setUploadMessage(
          attempt < maxRetries
            ? t('importModels.messages.retryAttempt', { attempt })
            : t('importModels.messages.uploadError')
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpen = () => setIsModalOpen(true);

  const handleClose = () => {
    setIsModalOpen(false);
    if (isUploadComplete) onCloseMenu();
    setUploadMessage('');
    setSelectedFiles([]);
    setModelName('');
    setUploadProgress(0);
    setIsUploadComplete(false);
    setModelNameError('');
  };

  return (
    <>
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        closeAfterTransition
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Fade in={isModalOpen}>
          <Box
            sx={{
              position: 'relative',
              backgroundColor: 'background.paper',
              boxShadow: 5,
              p: 4,
              borderRadius: 2,
              width: '500px',
              maxWidth: '100%',
              overflowY: 'auto',
              mx: 'auto',
              my: 'auto',
            }}>
            <IconButton
              aria-label={t('importModels.a11y.close')}
              onClick={handleClose}
              sx={{ position: 'absolute', right: 10, top: 10, color: '#999' }}
            >
              <CloseIcon />
            </IconButton>

            <div className="infoList--title">{ t('menu.importModels') }</div>

            <TextField
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              error={!!modelNameError}
              helperText={modelNameError}
              fullWidth
              required
              placeholder={t('importModels.placeholders.modelName')}
              sx={{ mb: 2.5, mt: 2 }}
            />

            <input
              type="file"
              multiple
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ marginBottom: 25 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ fontSize: '1.2rem' }}>{ t('importModels.labels.scale') }</InputLabel>
              <Select value={scale} label={t('importModels.labels.scale')} onChange={handleScaleChange}>
                <MenuItem value="1">1:1</MenuItem>
                <MenuItem value="10">1:10</MenuItem>
                <MenuItem value="100">1:100</MenuItem>
                <MenuItem value="1000">1:1000</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{ t('importModels.labels.rotation') }</InputLabel>
              <Select value={rotationY} label={t('importModels.labels.rotation')} onChange={handleRotationChange}>
                <MenuItem value={0}>0°</MenuItem>
                <MenuItem value={90}>90°</MenuItem>
                <MenuItem value={180}>180°</MenuItem>
                <MenuItem value={270}>270°</MenuItem>
              </Select>
            </FormControl>

            {uploadMessage && <Typography sx={{ mt: 2 }}>{uploadMessage}</Typography>}

            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>
                  {t('importModels.labels.progress', { percent: uploadProgress })}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <button onClick={isUploadComplete ? handleClose : uploadFiles} disabled={loading}>
                {isUploadComplete ? t('importModels.buttons.ok') : t('importModels.buttons.import')}
              </button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <div className="bm-item menu-item" onClick={handleOpen}>
        { t('menu.importModels') }
      </div>
    </>
  );
};

export default ImportModelsModal;
